import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { addResourceNameToQueryResult, useAxiosInstance } from './utils';
import type { ApiError } from './utils/types';

const PeriodTotalsSchema = z.object({
  period: z.string(),
  reminders: z.record(z.string(), z.number()),
  collections: z.number(),
});

const RemindersAndCollectionsSchema = z.object({ periods: z.array(PeriodTotalsSchema) });

export type Granularity = 'week' | 'month';

type LoadRemindersAndCollectionsVariables = { granularity: Granularity };

export type PeriodTotals = z.infer<typeof PeriodTotalsSchema>;
export type RemindersAndCollections = z.infer<typeof RemindersAndCollectionsSchema>;

async function loadRemindersAndCollectionsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadRemindersAndCollectionsVariables,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get('/analytics/reminders_and_collections', {
    params: variables,
  });
  return RemindersAndCollectionsSchema.parse(data);
}

export function useLoadRemindersAndCollections(variables: LoadRemindersAndCollectionsVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<RemindersAndCollections, ApiError>({
    queryKey: ['remindersAndCollections', variables],
    queryFn: () => loadRemindersAndCollectionsFn(axiosInstance, variables),
  });

  return addResourceNameToQueryResult<RemindersAndCollections, ApiError, 'remindersAndCollections'>(
    'remindersAndCollections',
    queryResult,
  );
}
